import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Calendar, Clock, Users, ArrowRight, Baby, Euro, Car, Bus, AlertCircle } from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar as CalendarComponent } from "../ui/calendar";
import { format } from "date-fns";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Locations for One-Way and Round Trip
const TRANSFER_LOCATIONS = {
  airports: {
    label: "Paris Airports",
    locations: [
      { value: "Charles de Gaulle Airport (CDG)", label: "Charles de Gaulle (CDG)" },
      { value: "Paris Orly Airport (ORY)", label: "Paris Orly (ORY)" },
      { value: "Beauvais Airport (BVA)", label: "Beauvais (BVA)" },
    ],
  },
  disney: {
    label: "Disneyland Paris",
    locations: [
      { value: "Disneyland Park", label: "Disneyland Park" },
      { value: "Hotel Near Disneyland", label: "Hotel Near Disneyland" },
      { value: "Apartment/Airbnb near Disneyland", label: "Apartment/Airbnb near Disneyland" },
    ],
  },
  paris: {
    label: "Paris",
    locations: [
      { value: "Paris City", label: "Paris City" },
      { value: "Paris Train Station", label: "Paris Train Station" },
      { value: "Paris Hotels", label: "Paris Hotels" },
      { value: "Paris Apartments/Airbnb", label: "Paris Apartments/Airbnb" },
    ],
  },
  attractions: {
    label: "Attractions",
    locations: [
      { value: "La Defense", label: "La Defense" },
      { value: "Chateau De Versailles", label: "Chateau De Versailles" },
      { value: "Versailles", label: "Versailles" },
    ],
  },
  tours: {
    label: "Tours",
    locations: [
      { value: "Mont Saint-Michel", label: "Mont Saint-Michel" },
      { value: "Normandy", label: "Normandy" },
    ],
  },
  other: {
    label: "Other",
    locations: [
      { value: "Other Location", label: "Other Location" },
    ],
  },
};

// Locations for Hourly bookings (no Tours)
const HOURLY_LOCATIONS = {
  airports: TRANSFER_LOCATIONS.airports,
  disney: TRANSFER_LOCATIONS.disney,
  paris: TRANSFER_LOCATIONS.paris,
  attractions: {
    label: "Attractions",
    locations: [
      { value: "La Defense", label: "La Defense" },
      { value: "Chateau De Versailles", label: "Chateau De Versailles" },
      { value: "Versailles", label: "Versailles" },
    ],
  },
  other: {
    label: "Other",
    locations: [
      { value: "Other Location", label: "Other Location" },
    ],
  },
};

// Generate time slots
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let min = 0; min < 60; min += 15) {
      const h = hour.toString().padStart(2, "0");
      const m = min.toString().padStart(2, "0");
      slots.push(`${h}:${m}`);
    }
  }
  return slots;
};

const TIME_SLOTS = generateTimeSlots();

const BookingWidget = ({ variant = "hero", prefilledData = {} }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [tripType, setTripType] = useState(prefilledData.tripType || searchParams.get("tripType") || "oneway");
  const [pickup, setPickup] = useState(prefilledData.pickup || searchParams.get("pickup") || "");
  const [dropoff, setDropoff] = useState(prefilledData.dropoff || searchParams.get("dropoff") || "");
  const [date, setDate] = useState(prefilledData.date ? new Date(prefilledData.date) : null);
  const [time, setTime] = useState(prefilledData.time || "");
  const [adults, setAdults] = useState(prefilledData.adults || "1");
  const [kids, setKids] = useState(prefilledData.kids || "0");
  const [hours, setHours] = useState(prefilledData.hours || "2");
  const [isDateOpen, setIsDateOpen] = useState(false);
  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [nightSurcharge, setNightSurcharge] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const totalPassengers = parseInt(adults) + parseInt(kids);
  
  // Check if "Other Location" is selected for either pickup or dropoff
  const isOtherLocation = pickup === "Other Location" || dropoff === "Other Location";
  const isQuoteRequired = isOtherLocation;
  
  // Check if night pricing applies (21:00 - 04:59)
  const isNightPricing = nightSurcharge > 0;
  
  // Get appropriate locations based on trip type
  const getLocations = () => {
    return tripType === "hourly" ? HOURLY_LOCATIONS : TRANSFER_LOCATIONS;
  };

  // Validate form - simplified for hero widget (no address validation)
  const validateForm = useCallback(() => {
    const errors = {};
    
    if (!pickup) errors.pickup = "Pickup location is required";
    if (!dropoff && tripType !== "hourly") errors.dropoff = "Drop-off location is required";
    if (!date) errors.date = "Date is required";
    if (!time) errors.time = "Time is required";
    if (!adults || adults === "0") errors.adults = "At least 1 adult is required";
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [pickup, dropoff, date, time, adults, tripType]);

  // Calculate price when relevant fields change
  useEffect(() => {
    const calculatePrice = async () => {
      // If "Other Location" is selected, don't calculate price - quote required
      if (isQuoteRequired) {
        setEstimatedPrice(null);
        setVehicleInfo(null);
        setNightSurcharge(0);
        return;
      }

      // Check mandatory fields
      if (!pickup || !date || !time || !adults || adults === "0") {
        setEstimatedPrice(null);
        setVehicleInfo(null);
        setNightSurcharge(0);
        return;
      }
      
      if (tripType !== "hourly" && !dropoff) {
        setEstimatedPrice(null);
        setVehicleInfo(null);
        setNightSurcharge(0);
        return;
      }

      setIsCalculating(true);
      try {
        const params = new URLSearchParams({
          trip_type: tripType,
          pickup_location: pickup,
          dropoff_location: dropoff || pickup,
          pickup_datetime: `${format(date, "yyyy-MM-dd")}T${time}:00`,
          adults: adults,
          kids: kids || "0",
        });
        
        if (tripType === "hourly") {
          params.set("hours", hours);
        }

        const response = await fetch(`${API_URL}/api/pricing/calculate?${params}`, {
          method: "POST",
        });

        if (response.ok) {
          const data = await response.json();
          setEstimatedPrice(data.total_price);
          setNightSurcharge(data.night_surcharge || 0);
          setVehicleInfo({
            type: data.vehicle_type,
            vehicles: data.vehicles_needed,
            breakdown: data.breakdown,
          });
        }
      } catch (error) {
        console.error("Error calculating price:", error);
      } finally {
        setIsCalculating(false);
      }
    };

    calculatePrice();
  }, [pickup, dropoff, date, time, adults, kids, tripType, hours, isQuoteRequired]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    // Pass selected values to booking form - address will be entered there
    const params = new URLSearchParams();
    params.set("tripType", tripType);
    if (pickup) params.set("pickup", pickup);
    if (dropoff) params.set("dropoff", dropoff);
    if (date) params.set("date", format(date, "yyyy-MM-dd"));
    if (time) params.set("time", time);
    params.set("adults", adults);
    params.set("kids", kids);
    if (tripType === "hourly") params.set("hours", hours);
    
    // Pass calculated price and night surcharge for consistency
    if (estimatedPrice !== null) {
      params.set("price", String(estimatedPrice));
    }
    if (nightSurcharge > 0) {
      params.set("nightSurcharge", String(nightSurcharge));
    }
    
    // Flag if quote is required (Other Location selected)
    if (isQuoteRequired) {
      params.set("quoteRequired", "true");
    }

    navigate(`/booking?${params.toString()}`);
  };

  const isHero = variant === "hero";
  const locations = getLocations();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className={`bg-white rounded-2xl shadow-strong ${
        isHero ? "p-4 md:p-5" : "p-4"
      }`}
      data-testid="booking-widget"
    >
      {isHero && (
        <div className="mb-3">
          <h2 className="font-heading font-bold text-lg md:text-xl text-midnight-navy">
            Book Your Transfer
          </h2>
          <p className="text-slate-600 text-xs mt-0.5">Fixed prices · Pay driver on arrival</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Trip Type Selector */}
        <div className="flex gap-1 p-1 bg-paris-stone rounded-xl mb-3">
          {["oneway", "return", "hourly"].map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setTripType(type)}
              className={`flex-1 py-2 px-2 rounded-lg font-medium text-xs transition-all ${
                tripType === type
                  ? "bg-midnight-navy text-white shadow-md"
                  : "text-slate-600 hover:text-midnight-navy"
              }`}
              data-testid={`trip-type-${type}`}
            >
              {type === "oneway" ? "One-Way" : type === "return" ? "Round Trip" : "Hourly"}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {/* Pickup Location - NO address input in hero widget */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-700">
              <MapPin className="w-3 h-3 inline mr-1 text-eiffel-blue" />
              Pickup Location *
            </Label>
            <Select value={pickup} onValueChange={setPickup}>
              <SelectTrigger data-testid="pickup-select" className={`h-9 text-sm ${formErrors.pickup ? 'border-red-500' : ''}`}>
                <SelectValue placeholder="Select pickup" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(locations).map(([key, category]) => (
                  <SelectGroup key={key}>
                    <SelectLabel className="text-xs text-slate-500 uppercase tracking-wider">
                      {category.label}
                    </SelectLabel>
                    {category.locations.map((loc) => (
                      <SelectItem key={loc.value} value={loc.value}>
                        {loc.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Dropoff Location - NO address input in hero widget */}
          {tripType !== "hourly" && (
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-700">
                <MapPin className="w-3 h-3 inline mr-1 text-champagne-gold" />
                Drop-off Location *
              </Label>
              <Select value={dropoff} onValueChange={setDropoff}>
                <SelectTrigger data-testid="dropoff-select" className={`h-9 text-sm ${formErrors.dropoff ? 'border-red-500' : ''}`}>
                  <SelectValue placeholder="Select drop-off" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(locations).map(([key, category]) => (
                    <SelectGroup key={key}>
                      <SelectLabel className="text-xs text-slate-500 uppercase tracking-wider">
                        {category.label}
                      </SelectLabel>
                      {category.locations.map((loc) => (
                        <SelectItem key={loc.value} value={loc.value}>
                          {loc.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Hours selector for hourly bookings */}
          {tripType === "hourly" && (
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-700">
                <Clock className="w-3 h-3 inline mr-1 text-champagne-gold" />
                Duration (hours)
              </Label>
              <Select value={hours} onValueChange={setHours}>
                <SelectTrigger data-testid="hours-select" className="h-9 text-sm">
                  <SelectValue placeholder="Select hours" />
                </SelectTrigger>
                <SelectContent>
                  {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((h) => (
                    <SelectItem key={h} value={String(h)}>
                      {h} hours
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Date */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-700">
              <Calendar className="w-3 h-3 inline mr-1 text-eiffel-blue" />
              Date *
            </Label>
            <Popover open={isDateOpen} onOpenChange={setIsDateOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={`w-full justify-start text-left font-normal h-9 text-sm border-slate-200 ${formErrors.date ? 'border-red-500' : ''}`}
                  data-testid="date-picker-trigger"
                >
                  {date ? format(date, "PPP") : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={date}
                  onSelect={(d) => {
                    setDate(d);
                    setIsDateOpen(false);
                  }}
                  disabled={(date) => date < new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-700">
              <Clock className="w-3 h-3 inline mr-1 text-eiffel-blue" />
              Time *
            </Label>
            <Select value={time} onValueChange={setTime}>
              <SelectTrigger data-testid="time-select" className={`h-9 text-sm ${formErrors.time ? 'border-red-500' : ''}`}>
                <SelectValue placeholder="Select time" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {TIME_SLOTS.map((slot) => (
                  <SelectItem key={slot} value={slot}>
                    {slot}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Adults */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-700">
              <Users className="w-3 h-3 inline mr-1 text-eiffel-blue" />
              Adults *
            </Label>
            <Select value={adults} onValueChange={setAdults}>
              <SelectTrigger data-testid="adults-select" className={`h-9 text-sm ${formErrors.adults ? 'border-red-500' : ''}`}>
                <SelectValue placeholder="Adults" />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16].map((num) => (
                  <SelectItem key={num} value={String(num)}>
                    {num} {num === 1 ? "Adult" : "Adults"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Kids */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-700">
              <Baby className="w-3 h-3 inline mr-1 text-eiffel-blue" />
              Kids (0-12)
            </Label>
            <Select value={kids} onValueChange={setKids}>
              <SelectTrigger data-testid="kids-select" className="h-9 text-sm">
                <SelectValue placeholder="Kids" />
              </SelectTrigger>
              <SelectContent>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                  <SelectItem key={num} value={String(num)}>
                    {num} {num === 1 ? "Child" : "Children"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Price Display or Quote Required Notice */}
        {isQuoteRequired ? (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <div>
                <span className="font-semibold text-amber-800 text-sm">Quote Required</span>
                <p className="text-xs text-amber-700 mt-0.5">
                  Custom locations require a personalized quote. We'll contact you with pricing.
                </p>
              </div>
            </div>
          </div>
        ) : estimatedPrice !== null && (
          <div className="mt-3 p-3 bg-paris-stone rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm text-slate-600">
                  {tripType === "hourly" ? (
                    <>
                      {hours} hours × €{totalPassengers <= 3 ? '60' : '70'}/hr
                    </>
                  ) : (
                    "Estimated Total:"
                  )}
                </span>
                {vehicleInfo && vehicleInfo.vehicles && (
                  <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                    {vehicleInfo.vehicles.cars > 0 && (
                      <span className="flex items-center gap-1">
                        <Car className="w-3 h-3" />
                        {vehicleInfo.vehicles.cars} Car{vehicleInfo.vehicles.cars > 1 ? 's' : ''}
                      </span>
                    )}
                    {vehicleInfo.vehicles.vans > 0 && (
                      <span className="flex items-center gap-1">
                        <Bus className="w-3 h-3" />
                        {vehicleInfo.vehicles.vans} Van{vehicleInfo.vehicles.vans > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <span className="font-heading font-bold text-xl text-midnight-navy">
                {isCalculating ? "..." : `€${estimatedPrice}`}
              </span>
            </div>
            {/* Night Pricing Notice */}
            {isNightPricing && (
              <div className="mt-2 pt-2 border-t border-slate-200 flex items-center gap-2">
                <Clock className="w-3 h-3 text-indigo-600" />
                <span className="text-xs text-indigo-700 font-medium">
                  Night pricing (+€{nightSurcharge}) applied for pickups 22:00–06:00
                </span>
              </div>
            )}
          </div>
        )}

        {/* Validation message */}
        {Object.keys(formErrors).length > 0 && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <span className="text-xs text-red-600">Please fill all required fields (*)</span>
          </div>
        )}

        {/* Submit Button - Dynamic based on quote requirement */}
        <Button
          type="submit"
          className={`w-full mt-3 font-bold py-3 rounded-full shadow-md hover:shadow-lg transition-all text-sm ${
            isQuoteRequired 
              ? "bg-amber-500 hover:bg-amber-600 text-white"
              : "bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy"
          }`}
          data-testid="widget-continue-btn"
        >
          {isQuoteRequired ? "Get a Quote" : "Book Your Private Transfer Now"}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>

        {/* Trust Badges */}
        <div className="mt-2 flex flex-wrap justify-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
            Free child seats
          </span>
          <span className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
            Fixed prices
          </span>
          <span className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
            Pay driver
          </span>
        </div>
      </form>
    </motion.div>
  );
};

export default BookingWidget;
